import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { updateWhere } from 'lib/array/arrayUtils'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'

import ThingsToKnowLayout from 'components/Common/ThingsToKnow/ThingsToKnowLayout'
import type { ThingsToKnowLayoutMode } from 'components/Common/ThingsToKnow/ThingsToKnowLayout'
import { mapFinePrintSubsectionsToThingsToKnowRows } from './finePrintSubsectionRows'

const INCLUSIONS_ROW_KEY = 'inclusions'
const FINE_PRINT_ROW_KEYS_AFTER_INCLUSIONS = new Set(['policies', 'cancellation', 'date-changes', 'fine-print'])

interface Props {
  property: App.Property.Property
  mode?: ThingsToKnowLayoutMode
}

function insertInclusionsRow(
  finePrintSubsections: Array<App.Property.FinePrintSubsection>,
  inclusionsRow: App.Property.FinePrintSubsection,
): Array<App.Property.FinePrintSubsection> {
  const nextSectionIndex = finePrintSubsections.findIndex(({ key }) => FINE_PRINT_ROW_KEYS_AFTER_INCLUSIONS.has(key))

  if (nextSectionIndex < 0) {
    return [...finePrintSubsections, inclusionsRow]
  }

  return [
    ...finePrintSubsections.slice(0, nextSectionIndex),
    inclusionsRow,
    ...finePrintSubsections.slice(nextSectionIndex),
  ]
}

function appendLoyaltyInclusions(
  finePrintSubsections: Array<App.Property.FinePrintSubsection>,
  loyaltyInclusions: Array<App.OfferInclusion>,
  inclusionsLabel: string,
): Array<App.Property.FinePrintSubsection> {
  if (!loyaltyInclusions.length) return finePrintSubsections

  const loyaltyLines = loyaltyInclusions.map(inclusion => `- ${inclusion.description}`).join('\n')
  const inclusionsIndex = finePrintSubsections.findIndex(row => row.key === INCLUSIONS_ROW_KEY)

  if (inclusionsIndex >= 0) {
    return updateWhere(
      ({ key }) => key === INCLUSIONS_ROW_KEY,
      (row) => ({
        ...row,
        content: {
          type: 'markdown',
          value: `${row.content.value}\n\n${loyaltyLines}`,
        },
      }),
      finePrintSubsections,
    )
  }

  return insertInclusionsRow(finePrintSubsections, {
    key: INCLUSIONS_ROW_KEY,
    icon: 'line_drinks',
    label: inclusionsLabel,
    content: {
      type: 'markdown',
      value: loyaltyLines,
    },
    collapsable: true,
  })
}

function PropertyThingsToKnow({ property, mode }: Props) {
  const intl = useIntl()
  const loyaltyInclusions = useLoyaltyBenefitInclusions(property)
  const inclusionsLabel = intl.formatMessage({
    id: 'jC65d7l0oyo06NoTAVcm',
    defaultMessage: 'Inclusions',
  })
  const finePrintSubsections = useMemo(
    () => appendLoyaltyInclusions(property.finePrintSubsections ?? [], loyaltyInclusions, inclusionsLabel),
    [property.finePrintSubsections, loyaltyInclusions, inclusionsLabel],
  )
  const rows = useMemo(
    () => mapFinePrintSubsectionsToThingsToKnowRows(finePrintSubsections),
    [finePrintSubsections],
  )

  return <ThingsToKnowLayout rows={rows} mode={mode} />
}

export default PropertyThingsToKnow
